/**
 * Settlement FX contract (router#3468) — how the CNY amounts on a billing
 * export or a monthly statement are derived from the USD the ledger stores.
 *
 * `exchange_rate_policy` USD_CNY (a platform-wide constant, currently 7) is
 * the only rate the system knew before this contract. A credit customer may
 * settle on a contract rule instead, so the rate becomes a per-account fact
 * with one read surface, shared by the tenant export and the operator
 * statement scripts.
 *
 * ── Versioned rules ────────────────────────────────────────────────────────
 *
 * A rule is a ROW per `(account_id, effective_from_month)`, not a single
 * mutable row. The rule in force for month M is the row with the greatest
 * `effective_from_month <= M`; no row at or before M means
 * `platform_policy`. Writing a rule for a later month therefore never rewrites
 * what an earlier month's exported invoice already said, and REPLACING a
 * version that is already the current month's version is a backdated
 * correction too — that month may already have been exported.
 *
 * ── The three rules ───────────────────────────────────────────────────────
 *
 *   - `platform_policy` — the default when no row is in force. Every month
 *     uses `exchange_rate_policy` USD_CNY for both model classes.
 *   - `cfets_first_workday` — USD-priced models use the CFETS USD/CNY central
 *     parity of the first working day of the service month (Asia/Shanghai
 *     month; 2026-09 → 2026-09-01 → 6.7809). CNY-priced models keep their CNY
 *     list price: their stored USD is converted back at
 *     {@link SETTLEMENT_CNY_PRICE_DIVISOR}, never at the contract rate.
 *   - `fixed` — a contract-fixed CNY-per-USD for USD-priced models; CNY-priced
 *     models again use {@link SETTLEMENT_CNY_PRICE_DIVISOR}.
 *
 * A month whose CFETS rate was never fetched is reported `available: false`
 * and must not be converted; it never silently falls back to
 * `exchange_rate_policy`, because a statement must not invent a rate.
 */

/**
 * Per-account settlement rule. `platform_policy` is also the implicit default
 * (no rule row in force for the month); the other two are explicit contract
 * rules.
 */
export type SettlementFxRule = 'platform_policy' | 'cfets_first_workday' | 'fixed';

/** Every rule value, in the order the operator surface lists them. */
export const SETTLEMENT_FX_RULES: readonly SettlementFxRule[] = ['platform_policy', 'cfets_first_workday', 'fixed'];

/**
 * The divisor that converts a CNY-priced model's stored USD back to CNY.
 *
 * Deliberately a constant, NOT `exchange_rate_policy`: it mirrors the CNY→USD
 * listing convention recorded in the statement script's
 * `pricing-currency.json` (`cny_price_divisor: 7`), which is what the stored
 * USD was derived from. Changing this value re-prices every contract
 * customer's CNY-priced history, so change it as a deliberate data migration,
 * never as a side effect of moving the platform policy.
 */
export const SETTLEMENT_CNY_PRICE_DIVISOR = 7;

/**
 * Plausible USD/CNY band for an operator override or a fetched CFETS parity.
 *
 * Both the platform override route and the control-loops fetcher enforce the
 * same inclusive bounds: a value outside them is a unit/column mistake, never
 * a rate this platform may ingest. Keeping the constants here is what makes
 * the two enforcement points provably identical.
 */
export const SETTLEMENT_FX_USD_CNY_MIN = 5;
export const SETTLEMENT_FX_USD_CNY_MAX = 9;

/** How a stored monthly rate was obtained: fetched from CFETS, or set by an operator. */
export type SettlementFxMonthlySource = 'cfets' | 'manual';

/**
 * Where a month's `usdPricedCnyPerUsd` came from:
 * `exchange_rate_policy` (the default/divisor), a monthly row (`cfets` /
 * `manual`), or a contract `fixed` rate.
 */
export type SettlementFxRateSource = 'exchange_rate_policy' | SettlementFxMonthlySource | 'fixed';

/** Largest range `GET /v1/billing/export-rates` answers in one request (inclusive months). */
export const SETTLEMENT_FX_MAX_MONTHS = 24;

// ─── Public (tenant) — camelCase ────────────────────────────────────────

/**
 * One service month of the caller's export rates.
 *
 * `usdPricedCnyPerUsd` converts USD list prices; `cnyPricedCnyPerUsd`
 * converts models whose official price is denominated in CNY. A null value
 * means that class has no rate for the month; `available: false` means the
 * client must not convert the month at all, because at least one of the two
 * conversions is unavailable. For a contract rule (cfets/fixed) the month is
 * also unavailable while BOTH model lists are empty — without a seeded list
 * there is no way to tell which models may be converted at the contract rate,
 * so nothing may.
 *
 * `rateDate` describes the monthly row the USD-priced rate came from (null
 * for `platform_policy` / `fixed`); `source` names the origin of that rate
 * (`exchange_rate_policy`, the row's `cfets`/`manual`, or `fixed`).
 *
 * There is deliberately no `note` here: the operator's note is internal
 * context and must not reach every member of the org. Operators read it from
 * the rules endpoint, which is session-gated.
 */
export interface BillingExportRateMonth {
  /** YYYY-MM, Asia/Shanghai service month. */
  readonly month: string;
  readonly rule: SettlementFxRule;
  readonly usdPricedCnyPerUsd: number | null;
  readonly cnyPricedCnyPerUsd: number | null;
  /** YYYY-MM-DD for cfets/manual months. */
  readonly rateDate: string | null;
  readonly source: SettlementFxRateSource | null;
  /** false => do not convert this month. */
  readonly available: boolean;
}

export interface BillingExportRatesModel {
  readonly vendor: string;
  readonly model: string;
}

/**
 * `GET /v1/billing/export-rates?from=YYYY-MM&to=YYYY-MM` body. `from` / `to`
 * echo the requested range; `months` is one entry per inclusive month and is
 * never longer than `SETTLEMENT_FX_MAX_MONTHS`.
 *
 * The two model lists partition the operator-maintained classification. A
 * model in NEITHER list is unknown: it must not be converted, and the caller
 * reports it rather than guessing a class.
 */
export interface BillingExportRatesResponse {
  readonly from: string;
  readonly to: string;
  readonly months: readonly BillingExportRateMonth[];
  /** Models whose official list price is CNY. */
  readonly cnyPricedModels: readonly BillingExportRatesModel[];
  /** Models whose official list price is USD. */
  readonly usdPricedModels: readonly BillingExportRatesModel[];
}

// ─── Internal (platform /internal/ops/settlement-fx/*) — snake_case ─────

/**
 * One stored `settlement_fx_rules` row, verbatim. Rows are versions: the
 * unique key is `(account_id, effective_from_month)` and the row in force for
 * a month is the greatest `effective_from_month <= month`.
 */
export interface SettlementFxRuleRecord {
  readonly account_id: string;
  readonly rule: SettlementFxRule;
  readonly fixed_cny_per_usd: number | null;
  readonly effective_from_month: string;
  readonly note: string;
  readonly updated_at: string;
  readonly updated_by: string;
}

/**
 * `GET /internal/ops/settlement-fx/rules/:accountId` body.
 *
 * `current` is the version in force for the current Asia/Shanghai service
 * month (null when no version starts early enough, which means
 * `platform_policy`); `history` is every version, oldest first.
 */
export interface GetSettlementFxRuleResponse {
  readonly account_id: string;
  readonly current: SettlementFxRuleRecord | null;
  readonly history: readonly SettlementFxRuleRecord[];
}

/**
 * `PUT /internal/ops/settlement-fx/rules/:accountId` body. The row is keyed by
 * `(account_id, effective_from_month)`; writing an existing month replaces
 * that version only.
 *
 * - `note` is required (1–500 chars): it is the operator's reason, and the
 *   audit row carries it.
 * - An `effective_from_month` earlier than the current Asia/Shanghai month is
 *   a backdated correction: it requires `backdate: true` AND a non-empty
 *   `backdate_reason` (≤ 500 chars), which the audit metadata records.
 * - REPLACING an existing version whose `effective_from_month` equals the
 *   current Asia/Shanghai month also requires those fields, for the same
 *   reason (the month may already have been exported). Creating the
 *   current-month version for the first time, or writing any future version,
 *   needs neither.
 * - `fixed_cny_per_usd` is required exactly for `rule: 'fixed'` and must be
 *   absent/null otherwise (the table's
 *   `settlement_fx_rules_fixed_rate_iff_fixed` CHECK).
 */
export interface UpsertSettlementFxRuleRequest {
  readonly rule: SettlementFxRule;
  readonly fixed_cny_per_usd?: number | null;
  readonly effective_from_month: string;
  readonly note: string;
  readonly backdate?: boolean;
  readonly backdate_reason?: string;
}

/** Stored `settlement_fx_monthly_rates` row, verbatim. */
export interface SettlementFxMonthlyRateRecord {
  readonly pair: 'USD_CNY';
  readonly month: string;
  readonly cny_per_usd: number;
  readonly rate_date: string;
  readonly source: SettlementFxMonthlySource;
  readonly note: string | null;
  readonly fetched_at: string;
  readonly updated_by: string;
}

export interface ListSettlementFxMonthlyRatesResponse {
  readonly rates: readonly SettlementFxMonthlyRateRecord[];
}

/**
 * `PUT /internal/ops/settlement-fx/monthly-rates/:month` body — an operator
 * override. Written with `source: 'manual'`, audited. The month may not be
 * later than the current Asia/Shanghai month and `rate_date` may not be later
 * than today in Shanghai.
 *
 * A month earlier than the current Asia/Shanghai month is a backdated
 * correction: it requires `backdate: true` AND a non-empty `backdate_reason`
 * (≤ 500 chars), both recorded in the audit metadata. `cny_per_usd` must be
 * inside the shared USD/CNY band
 * [{@link SETTLEMENT_FX_USD_CNY_MIN}, {@link SETTLEMENT_FX_USD_CNY_MAX}].
 *
 * A manual row for a month blocks the later automatic fetch for that month
 * (the ingest is insert-only), so the operator value stays authoritative.
 */
export interface PutSettlementFxMonthlyRateRequest {
  readonly cny_per_usd: number;
  readonly rate_date: string;
  readonly note: string;
  readonly backdate?: boolean;
  readonly backdate_reason?: string;
}

/**
 * `POST /internal/ops/settlement-fx/monthly-rates/fetched` body — the
 * control-loops fetcher's ingest of the first-working-day parity. Insert-only:
 * a month that already has a row (fetched or operator-set) is never
 * overwritten, hence the `inserted` / `existing` response below.
 */
export interface PostSettlementFxFetchedRateRequest {
  readonly month: string;
  readonly cny_per_usd: number;
  readonly rate_date: string;
  readonly raw_json: string;
}

export interface PostSettlementFxFetchedRateResponse {
  readonly inserted: boolean;
  /** The row that was already there when `inserted` is false; otherwise null. */
  readonly existing: SettlementFxMonthlyRateRecord | null;
}

/** Stored `settlement_cny_priced_models` row, verbatim. */
export interface SettlementCnyPricedModelRecord {
  readonly vendor: string;
  readonly model: string;
  readonly cny_priced: boolean;
  readonly note: string | null;
  readonly updated_at: string;
  readonly updated_by: string;
}

export interface ListSettlementCnyPricedModelsResponse {
  readonly models: readonly SettlementCnyPricedModelRecord[];
}

/**
 * `PUT /internal/ops/settlement-fx/cny-priced-models` body — the full list to
 * upsert (operator-maintained; unknown models are reported as unknown, never
 * guessed). `cny_priced: false` is a real answer: it classifies a model as
 * USD-priced, which is what keeps it convertible at the contract rate.
 */
export interface PutSettlementCnyPricedModelsRequest {
  readonly models: readonly { readonly vendor: string; readonly model: string; readonly cny_priced: boolean; readonly note?: string | null }[];
}

// ─── Pure helpers ───────────────────────────────────────────────────────

const SETTLEMENT_MONTH_PATTERN = /^[0-9]{4}-(0[1-9]|1[0-2])$/;

/** True for a `YYYY-MM` service month with a real month 01–12. */
export function isSettlementMonth(value: string): boolean {
  return SETTLEMENT_MONTH_PATTERN.test(value);
}

/** A validated month as a monotonic month index (`year * 12 + month - 1`). */
function settlementMonthIndex(value: string): number | null {
  if (!isSettlementMonth(value)) return null;
  return Number(value.slice(0, 4)) * 12 + Number(value.slice(5, 7)) - 1;
}

/**
 * The inclusive list of `YYYY-MM` months from `from` to `to`.
 *
 * Returns null — never a partial or clamped range — when either endpoint is
 * not a valid month, when `from` is after `to`, or when the span is longer
 * than `SETTLEMENT_FX_MAX_MONTHS`. Callers turn null into their own 400.
 */
export function settlementMonthsBetween(from: string, to: string): readonly string[] | null {
  const fromIndex = settlementMonthIndex(from);
  const toIndex = settlementMonthIndex(to);
  if (fromIndex === null || toIndex === null || fromIndex > toIndex) return null;
  if (toIndex - fromIndex + 1 > SETTLEMENT_FX_MAX_MONTHS) return null;
  const months: string[] = [];
  for (let index = fromIndex; index <= toIndex; index += 1) {
    const year = String(Math.floor(index / 12)).padStart(4, '0');
    const month = String((index % 12) + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
  }
  return months;
}

/** True for one of the three stored rule values. */
export function isSettlementFxRule(value: unknown): value is SettlementFxRule {
  return value === 'platform_policy' || value === 'cfets_first_workday' || value === 'fixed';
}
